import { format, isToday, isYesterday } from 'date-fns';
export const changeDate = (date, offset) => {
    const changedDate = new Date(date);
    changedDate.setDate(changedDate.getDate() + offset);
    return changedDate;
};
export const dateWithNoTime = (date, lowercase) => {
    if (isToday(date)) {
        return lowercase ? 'today' : 'Today';
    }
    if (isYesterday(date)) {
        return lowercase ? 'yesterday' : 'Yesterday';
    }
    const currentYear = new Date().getFullYear();
    const yearToShow = date.getFullYear();
    let dateFormat;
    if (yearToShow === currentYear) {
        // If the year is the same as the current year, only display the month and day
        dateFormat = { month: 'long', day: 'numeric' };
    }
    else {
        // If the year is different, display the full date with the year
        dateFormat = { year: 'numeric', month: 'long', day: 'numeric' };
    }
    return date.toLocaleString('default', dateFormat);
};
export const dateWithNoTimeDashed = (date) => format(date, 'yyyy-MM-dd');
/** True when `yyyy-MM-dd` matches the device’s local calendar today. */
export function isLocalCalendarToday(dateStr, now = new Date()) {
    return dateStr === dateWithNoTimeDashed(now);
}
/**
 * Parse a `yyyy-MM-dd` string into a Date at **local** midnight.
 *
 * This is the safe way to turn a date-only string back into a Date. Using
 * `new Date('2026-07-03')` parses it as UTC midnight, which then renders as the
 * *previous* day in negative-offset time zones (e.g. "Thu, Jul 2" in US Eastern)
 * — the classic source of "the app shows yesterday" bugs. Building the Date from
 * local components avoids that shift entirely, in any time zone.
 *
 * Returns `null` if the string is not a `yyyy-MM-dd` date. When you need a
 * guaranteed Date, use {@link convertNoTimeDashedToDate}.
 */
export const parseDashedDateLocal = (dateStr) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!match) {
        return null;
    }
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
};
/**
 * Convert a `yyyy-MM-dd` string to a Date at **local** midnight (never null;
 * falls back to `new Date(dateStr)` for non-dashed input). Prefer
 * {@link parseDashedDateLocal} when you can handle malformed input explicitly.
 */
export const convertNoTimeDashedToDate = (dateStr) => parseDashedDateLocal(dateStr) ?? new Date(dateStr);
export const getFirstMonday = (date) => getWeekStart(date, 1);
/**
 * Start of the week containing `date` for the given weekStartsOn
 * (0=Sunday … 6=Saturday; default Monday).
 */
export const getWeekStart = (date, weekStartsOn = 1) => {
    const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = currentDate.getDay();
    const daysBack = (dayOfWeek - weekStartsOn + 7) % 7;
    currentDate.setDate(currentDate.getDate() - daysBack);
    return currentDate;
};
export function generateWeekDayNamesArray(startingDate, numberOfDays) {
    const daysOfWeek = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ];
    const result = [];
    let currentDate = new Date(startingDate);
    for (let i = 0; i < numberOfDays; i++) {
        const dayName = daysOfWeek[currentDate.getDay()];
        const dayOfMonth = currentDate.getDate();
        const formattedString = `${dayName} ${dayOfMonth < 10 ? '0' : ''}${dayOfMonth}`;
        result.push(formattedString);
        currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
    }
    return result;
}
export function generateWeekDateStringsArray(startingDate, numberOfDays) {
    const result = [];
    let currentDate = new Date(startingDate);
    for (let i = 0; i < numberOfDays; i++) {
        result.push(dateWithNoTimeDashed(currentDate));
        currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
    }
    return result;
}
/**
 * Day index within the week: 0 = first day of week (`weekStartsOn`).
 * Default Monday so existing callers stay Mon=0 … Sun=6.
 */
export const getDayIndex = (date, weekStartsOn = 1) => {
    return (date.getDay() - weekStartsOn + 7) % 7;
};
/**
 *
 * @param dateStr in the format mm-dd-yyyy
 * @returns a Date object or "Invalid Date" if format isn't right
 */
export function dateStringToDate(dateStr) {
    if (!dateStr) {
        return null;
    }
    const parts = dateStr.split('-');
    if (parts.length !== 3) {
        return null;
    }
    const [month, day, year] = parts.map(Number);
    if ([month, day, year].some(isNaN)) {
        return null;
    }
    // Explicit range checks
    if (month < 1 || month > 12) {
        return null;
    }
    if (day < 1 || day > 31) {
        return null;
    }
    const d = new Date(year, month - 1, day);
    // Ensure no rollover happened (check components match)
    if (d.getMonth() !== month - 1 ||
        d.getDate() !== day ||
        d.getFullYear() !== year) {
        return null;
    }
    return d;
}
export const calculateYearsAgo = (then, now) => {
    let yearsAgo = now.getFullYear() - then.getFullYear();
    const thisYear = now.getMonth() > then.getMonth() ||
        (now.getMonth() === then.getMonth() && now.getDate() >= then.getDate());
    // Subtract 1 only if "then" date has not occurred yet this year
    if (!thisYear) {
        yearsAgo--;
    }
    return yearsAgo;
};
/**
 * Calendar date and clock time with timezone, for logs and Slack reports.
 * Avoids ISO strings like `2026-08-23T10:48:12.345Z`.
 * Strings that are already readable (not ISO) are left unchanged.
 */
export function formatReadableDateTime(value, timeZone) {
    if (typeof value === 'string' &&
        !/^\d{4}-\d{2}-\d{2}T/.test(value.trim())) {
        return value;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    const options = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
    };
    if (timeZone) {
        options.timeZone = timeZone;
    }
    return date.toLocaleString('en-US', options);
}
export const humanFriendlyDateTime = (dateIn) => {
    const date = dateIn instanceof Date ? dateIn : new Date(dateIn);
    if (isToday(date)) {
        return `Today, at ${format(date, 'h:mm:ss a')}`;
    }
    if (isYesterday(date)) {
        return `Yesterday, at ${format(date, 'h:mm:ss a')}`;
    }
    const currentYear = new Date().getFullYear();
    const yearToShow = date.getFullYear();
    if (yearToShow === currentYear) {
        // If it's the same year, return only "Month Day at Time"
        return `${format(date, 'MMMM d')} at ${format(date, 'h:mm:ss a')}`;
    }
    else {
        // If it's a different year, return "Month Day, Year at Time"
        return `${format(date, 'MMMM do, yyyy')} at ${format(date, 'h:mm:ss a')}`;
    }
};
